/**
 * 睡眠视图数据工具函数
 * 包含纯函数方法，不依赖组件实例
 */

import * as sleepViewUtils from './sleepViewUtils';

/**
 * 解析时间字符串
 * @param {string} timeStr - 时间字符串
 * @returns {Date|null} 解析后的日期对象
 */
export function parseTimeString(timeStr) {
  return sleepViewUtils.parseTimeString(timeStr);
}

/**
 * 基于指定日期解析时间字符串
 * @param {string} timeStr - 时间字符串
 * @param {Date} appointmentDate - 预约日期
 * @returns {Date|null} 解析后的日期对象
 */
export function parseTimeStringWithDate(timeStr, appointmentDate) {
  return sleepViewUtils.parseTimeStringWithDate(timeStr, appointmentDate);
}

/**
 * 获取剩余时间（分钟数）
 * @param {Object} appointment - 预约对象
 * @returns {number} 剩余分钟数，如果未开始则返回很大的数字
 */
export function getRemainingTimeInMinutes(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return Number.MAX_SAFE_INTEGER;
  }

  const now = new Date();
  const startTime = parseTimeString(appointment.startTime);
  const endTime = parseTimeString(appointment.endTime);

  if (!startTime || !endTime) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (now >= endTime) {
    return 0;
  }

  if (now < startTime) {
    const totalMinutes = Math.floor(
      (endTime.getTime() - startTime.getTime()) / (1000 * 60)
    );
    return totalMinutes + 10000;
  }

  const remainingMs = endTime.getTime() - now.getTime();
  return Math.ceil(remainingMs / (1000 * 60));
}

/**
 * 获取实际剩余时间（格式化字符串）
 * @param {Object} appointment - 预约对象
 * @returns {string} 格式化的剩余时间字符串
 */
export function getActualRemainingTime(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return '无数据';
  }

  const now = new Date();
  const startTime = parseTimeString(appointment.startTime);
  const endTime = parseTimeString(appointment.endTime);

  if (!startTime || !endTime) {
    return '时间格式错误';
  }

  if (now >= endTime) {
    return '已完成';
  }

  if (now < startTime) {
    const totalMinutes = Math.floor(
      (endTime.getTime() - startTime.getTime()) / (1000 * 60)
    );
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0
      ? `${hours}小时${minutes}分钟`
      : `${minutes}分钟`;
  }

  const remainingMs = endTime.getTime() - now.getTime();
  const remainingMinutes = Math.ceil(remainingMs / (1000 * 60));

  if (remainingMinutes <= 0) {
    return '即将完成';
  }

  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  
  return hours > 0
    ? `${hours}小时${minutes}分钟`
    : `${minutes}分钟`;
}

/**
 * 获取睡眠气泡值（随机值）
 * @param {string} sleepStatus - 睡眠状态
 * @returns {number} 气泡值（1-3）
 */
export function getSleepBubbleValue(sleepStatus) {
  const random = Math.random() * 100; // 0-100的随机数
  let bubbleValue = 1; // 默认值
  
  switch (sleepStatus) {
    case '深睡':
      // 60%概率+3分，30%概率+2分，10%概率+1分
      if (random < 60) {
        bubbleValue = 3;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 1;
      }
      break;
    case '浅睡':
      // 30%概率+1分，60%概率+2分，10%概率+3分
      if (random < 30) {
        bubbleValue = 1;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 3;
      }
      break;
    case '清醒':
      // 90%概率+1分，10%概率+2分
      if (random < 90) {
        bubbleValue = 1;
      } else {
        bubbleValue = 2;
      }
      break;
    default:
      // 确保即使是未知状态也有气泡值
      bubbleValue = 1;
  }
  
  return bubbleValue;
}

/**
 * 生成基于患者信息的随机种子
 * @param {Object} item - 患者信息对象
 * @returns {number} 种子值
 */
export function generatePatientSeed(item) {
  // 使用患者姓名、房间号、开始时间等信息生成固定种子
  const seedString = `${item.patientName || ''}_${item.roomName || ''}_${item.startTime || ''}`;
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    const char = seedString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转换为32位整数
  }
  return Math.abs(hash);
}

/**
 * 从字符串生成数字种子
 * @param {string} name - 字符串
 * @returns {number} 种子值
 */
export function generateSeedFromName(name) {
  let seed = 0;
  for (let i = 0; i < name.length; i++) {
    seed += name.charCodeAt(i);
  }
  return seed;
}

/**
 * 基于种子生成指定范围内的值
 * @param {number} seed - 种子值
 * @param {number} min - 最小值
 * @param {number} max - 最大值
 * @returns {number} 生成的值
 */
export function generateSeededValue(seed, min, max) {
  // 简单的伪随机数生成器
  const x = Math.sin(seed) * 10000;
  const random = x - Math.floor(x);
  return min + random * (max - min);
}

const virtualBrainwaveProfileCache = new Map();

const VIRTUAL_ARCHETYPES = [
  {
    name: 'slow-wave',
    baseBias: { delta: 18, theta: 12, alpha: -8, beta: -12, gamma: -10 },
    amp: { delta: 1.4, theta: 1.2, alpha: 0.8, beta: 0.7, gamma: 0.6 },
    spikeChance: 0.03
  },
  {
    name: 'awake-dominant',
    baseBias: { delta: -10, theta: -6, alpha: 10, beta: 16, gamma: 8 },
    amp: { delta: 0.7, theta: 0.8, alpha: 1.1, beta: 1.3, gamma: 1.2 },
    spikeChance: 0.05
  },
  {
    name: 'mixed-theta-alpha',
    baseBias: { delta: 2, theta: 14, alpha: 12, beta: -6, gamma: -4 },
    amp: { delta: 0.9, theta: 1.3, alpha: 1.25, beta: 0.85, gamma: 0.8 },
    spikeChance: 0.04
  },
  {
    name: 'high-frequency',
    baseBias: { delta: -14, theta: -8, alpha: 4, beta: 12, gamma: 18 },
    amp: { delta: 0.6, theta: 0.7, alpha: 0.9, beta: 1.2, gamma: 1.5 },
    spikeChance: 0.07
  }
];

function hashStringToSeed(input) {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function getProfileContext(patientName, context = {}) {
  const roomName = context.roomName || context.roomId || 'no_room';
  const startTime = context.startTime || 'no_start';
  const appointmentDate = context.appointmentDate || 'no_date';
  return `${patientName || 'default'}_${roomName}_${startTime}_${appointmentDate}`;
}

function getSleepStatePattern(currentSleepState) {
  switch (currentSleepState) {
    case '清醒':
      return { delta: 4, theta: 10, alpha: 36, beta: 42, gamma: 8 };
    case '深睡':
      return { delta: 68, theta: 20, alpha: 7, beta: 3, gamma: 2 };
    case '浅睡':
    default:
      return { delta: 32, theta: 40, alpha: 16, beta: 8, gamma: 4 };
  }
}

function createVirtualProfile(patientName, context = {}) {
  const profileKey = getProfileContext(patientName, context);
  const baseSeed = hashStringToSeed(profileKey);
  const rand = (offset) => seededRandom(baseSeed + offset);
  const archetype = VIRTUAL_ARCHETYPES[baseSeed % VIRTUAL_ARCHETYPES.length];

  return {
    profileKey,
    minClamp: 0.6,
    driftScale: 0.08 + rand(11) * 0.2,
    spikeChance: archetype.spikeChance,
    spikeScale: 0.20 + rand(23) * 0.45,
    archetype,
    bands: {
      delta: { freq1: 0.20 + rand(1) * 0.8, freq2: 0.10 + rand(2) * 0.6, phase1: rand(3) * Math.PI * 2, phase2: rand(4) * Math.PI * 2, waveScale: 0.3 + rand(5) * 0.9, noiseScale: 0.12 + rand(6) * 0.45 },
      theta: { freq1: 0.35 + rand(7) * 1.3, freq2: 0.18 + rand(8) * 0.9, phase1: rand(9) * Math.PI * 2, phase2: rand(10) * Math.PI * 2, waveScale: 0.3 + rand(12) * 0.95, noiseScale: 0.12 + rand(13) * 0.5 },
      alpha: { freq1: 0.45 + rand(14) * 1.8, freq2: 0.25 + rand(15) * 1.2, phase1: rand(16) * Math.PI * 2, phase2: rand(17) * Math.PI * 2, waveScale: 0.35 + rand(18) * 1.0, noiseScale: 0.14 + rand(20) * 0.55 },
      beta: { freq1: 0.55 + rand(21) * 2.2, freq2: 0.30 + rand(22) * 1.5, phase1: rand(24) * Math.PI * 2, phase2: rand(25) * Math.PI * 2, waveScale: 0.35 + rand(26) * 1.1, noiseScale: 0.16 + rand(27) * 0.6 },
      gamma: { freq1: 0.60 + rand(28) * 2.8, freq2: 0.40 + rand(29) * 2.0, phase1: rand(30) * Math.PI * 2, phase2: rand(31) * Math.PI * 2, waveScale: 0.4 + rand(32) * 1.2, noiseScale: 0.18 + rand(33) * 0.65 }
    }
  };
}

function getVirtualProfile(patientName, context = {}) {
  const profileKey = getProfileContext(patientName, context);
  if (!virtualBrainwaveProfileCache.has(profileKey)) {
    virtualBrainwaveProfileCache.set(profileKey, createVirtualProfile(patientName, context));
  }
  return virtualBrainwaveProfileCache.get(profileKey);
}

/**
 * 生成虚拟脑波数据
 * @param {string} patientName - 患者姓名
 * @param {string} currentSleepState - 当前睡眠状态，默认为'浅睡'
 * @param {Object} context - 预约上下文
 * @returns {Object} 脑波数据对象
 */
export function generateVirtualBrainwaveData(patientName, currentSleepState = '浅睡', context = {}) {
  let state = currentSleepState;
  let ctx = context;
  if (typeof currentSleepState === 'object' && currentSleepState !== null) {
    ctx = currentSleepState;
    state = ctx.sleepState || ctx.realtimeSleepState || '浅睡';
  }

  const profile = getVirtualProfile(patientName, ctx);
  const pattern = getSleepStatePattern(state);
  const bands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
  const nowSeconds = Date.now() / 1000;
  const rawValues = {};

  bands.forEach((band, index) => {
    const cfg = profile.bands[band];
    const archetype = profile.archetype;
    const base = Math.max(0.5, pattern[band] + (archetype.baseBias[band] || 0));
    const harmonic = (
      Math.sin(nowSeconds * cfg.freq1 + cfg.phase1) * cfg.waveScale +
      Math.cos(nowSeconds * cfg.freq2 + cfg.phase2) * (cfg.waveScale * 0.75)
    ) * (archetype.amp[band] || 1);
    const randomNoise = (Math.random() - 0.5) * cfg.noiseScale;
    const slowDrift = Math.sin(nowSeconds * profile.driftScale + cfg.phase1 * 0.5) * 0.25;
    const spike = Math.random() < profile.spikeChance ? (Math.random() - 0.5) * profile.spikeScale : 0;
    const dynamicFactor = 1 + harmonic + randomNoise + slowDrift + spike;

    rawValues[band] = Math.max(profile.minClamp, base * dynamicFactor + index * 0.2);
  });

  const total = Object.values(rawValues).reduce((sum, val) => sum + val, 0);
  const normalizedValues = {};
  bands.forEach((band) => {
    normalizedValues[band] = Math.max(0.5, Math.round(((rawValues[band] / total) * 100) * 10) / 10);
  });

  const normalizedTotal = Object.values(normalizedValues).reduce((sum, val) => sum + val, 0);
  const diff = Math.round((100 - normalizedTotal) * 10) / 10;
  if (diff !== 0) {
    const maxBand = bands.reduce((a, b) => (normalizedValues[a] > normalizedValues[b] ? a : b));
    normalizedValues[maxBand] = Math.max(0.5, Math.round((normalizedValues[maxBand] + diff) * 10) / 10);
  }

  return {
    delta: normalizedValues.delta,
    theta: normalizedValues.theta,
    alpha: normalizedValues.alpha,
    beta: normalizedValues.beta,
    gamma: normalizedValues.gamma,
    // 添加虚拟数据标识
    isVirtualBrainwave: true
  };
}

/**
 * 格式化患者姓名
 * @param {string} patientName - 患者姓名
 * @returns {string} 格式化后的姓名
 */
export function formatPatientName(patientName) {
  return sleepViewUtils.formatPatientName(patientName);
}

/**
 * 获取治疗状态
 * @param {Object} appointment - 预约对象
 * @returns {string} 治疗状态
 */
export function getTreatmentStatus(appointment) {
  return sleepViewUtils.getTreatmentStatus(appointment);
}

